import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Users, TrendingUp, DollarSign, Workflow, Target, Trophy, Loader2,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";

const money = (n) => "$" + (n || 0).toLocaleString();

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="p-5 bg-card border-border/70 hover:-translate-y-0.5 transition-transform duration-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
          <p className="text-3xl font-display font-bold mt-2">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { activeSubId, activeSub, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeSubId) return;
    setLoading(true);
    api.get(`/dashboard?sub_account_id=${activeSubId}`).then(({ data }) => {
      setData(data); setLoading(false);
    }).catch(() => setLoading(false));
  }, [activeSubId]);

  if (!loading && !data)
    return <p className="text-sm text-muted-foreground py-16" data-testid="dashboard-error">Couldn't load this dashboard. Try switching client or refreshing.</p>;
  if (loading || !data)
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const brand = activeSub?.branding?.primary_color || "#6366F1";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="relative overflow-hidden rounded-2xl border border-border/60 p-6 sm:p-8"
        style={{ background: "linear-gradient(120deg, #111827 0%, #0b1220 55%, #090D16 100%)" }}>
        <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: brand }} />
        <div className="relative z-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{activeSub?.name}</p>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">{greeting}, {firstName}</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">Here's how {activeSub?.name} is performing today.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/contacts")} className="gap-2 font-semibold" data-testid="quick-add-contact"><Users className="w-4 h-4" />Add contact</Button>
            <Button onClick={() => navigate("/pipelines")} variant="outline" className="gap-2 bg-white/5" data-testid="quick-new-deal"><Target className="w-4 h-4" />New deal</Button>
            <Button onClick={() => navigate("/automations")} variant="outline" className="gap-2 bg-white/5" data-testid="quick-build-automation"><Workflow className="w-4 h-4" />Automate</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Kpi icon={Users} label="Total Contacts" value={data.total_contacts} accent="#6366F1" testid="kpi-contacts" />
        <Kpi icon={DollarSign} label="Revenue (Won)" value={money(data.revenue)} accent="#10B981" testid="kpi-revenue" />
        <Kpi icon={Target} label="Open Pipeline" value={money(data.pipeline_value)} accent="#06B6D4" testid="kpi-pipeline" />
        <Kpi icon={TrendingUp} label="Conversion Rate" value={`${data.conversion_rate}%`} accent="#F59E0B" testid="kpi-conversion" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 bg-card border-border/70" data-testid="leads-trend-card">
          <h3 className="font-display font-semibold text-lg mb-1">Leads generated</h3>
          <p className="text-sm text-muted-foreground mb-6">New contacts over the last 7 days</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.leads_trend}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={brand} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={brand} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 10, color: "#fff" }} />
              <Area type="monotone" dataKey="leads" stroke={brand} strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 bg-card border-border/70" data-testid="deal-status-card">
          <h3 className="font-display font-semibold text-lg mb-6">Deals</h3>
          <div className="space-y-4">
            {[
              { label: "Open", value: data.open_deals, icon: Target, color: "#06B6D4" },
              { label: "Won", value: data.won_deals, icon: Trophy, color: "#10B981" },
              { label: "Lost", value: data.lost_deals, icon: Target, color: "#F43F5E" },
              { label: "Automations running", value: data.active_automations, icon: Workflow, color: "#8B5CF6" },
            ].map((r) => (
              <div key={r.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${r.color}22` }}>
                  <r.icon className="w-4 h-4" style={{ color: r.color }} />
                </div>
                <span className="text-sm text-muted-foreground flex-1">{r.label}</span>
                <span className="font-display font-bold text-lg">{r.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {data.pipeline_breakdown?.map((p) => (
        <Card key={p.name} className="p-6 bg-card border-border/70" data-testid="pipeline-breakdown-card">
          <h3 className="font-display font-semibold text-lg mb-6">{p.name} · {p.total} deals</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={p.stages}>
              <XAxis dataKey="name" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 10, color: "#fff" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {p.stages.map((_, i) => <Cell key={i} fill={brand} fillOpacity={0.5 + (i / p.stages.length) * 0.5} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      ))}
    </div>
  );
}
